import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cake, Check } from 'lucide-react';
import { getBirthdays, Birthday } from '../members/api';
import PersonAvatar from '../../components/PersonAvatar';
import { teacherPhotoUrl } from '../teachers/api';
import ClassChips from '../../components/ClassChips';
import { readCache, writeCache, pruneCacheGroup } from '../../services/localCache';
import './BirthdayAlert.css';

// 🚨 AQUÍ YA NO SE FELICITA A NADIE. Carlos retiró el botón de WhatsApp del Home el 7-ago; con él
// se fueron la plantilla del mensaje (`birthday_message`), `greetMember` y el marcado de
// felicitado, que aquí quedaban sin quien los llamara. NO se perdió la función: el muralito
// `/cumpleanos` (BirthdaysMonthPage) tiene el botón completo, con WhatsApp y con «Marcar», y
// desde aquí se llega en un clic. Si el botón vuelve, se repone mirando el commit que lo quitó.

/** Días hacia atrás que se siguen mostrando. Solo AYER (Carlos 7-ago): con 7 la barra se llenaba
 *  de gente que cumplió la semana pasada y desplazaba a los que vienen, que es lo accionable. */
const DIAS_ATRAS = 1;
/** Un color por estado, para no tener que leer la tarjeta para saber cuál es cuál (Carlos 7-ago).
 *  Cálido→frío según lo urgente: hoy dorado, ayer ámbar, lo que viene azul. */
const TONO = {
  hoy: 'border-accent/60 bg-accent/10',
  ayer: 'border-amber-400/50 bg-amber-400/10',
  proximo: 'border-brand-blue/30 bg-brand-blue/5',
};

/** Cuántas tarjetas caben antes de que la barra deje de ser una barra. El resto, en el muralito.
 *  4 = UNA sola fila de aquí para arriba. Con 6 salían dos filas y el aviso pasaba de 312px de
 *  alto, empujando el calendario fuera de la primera pantalla: un recordatorio no puede tapar
 *  aquello para lo que se abre el sistema. */
const MAX_TARJETAS = 4;

/** YYYY-MM-DD local — days_until cambia por día, el caché debe ser del MISMO día. */
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** «ayer», «en 3d»… — lo que se lee de un vistazo, sin tener que restar fechas.
 *  Los plazos van abreviados porque la línea entera («lun 3 ago · hace 4 días · 37 años») no
 *  cabía en una tarjeta de cuatro por fila y se cortaba justo en la edad. */
function relativo(dias: number) {
  if (dias === 0) return 'hoy';
  if (dias === -1) return 'ayer';
  if (dias === 1) return 'mañana';
  return dias < 0 ? `hace ${-dias}d` : `en ${dias}d`;
}

/** Tolera que falte bday_date: un caché escrito antes de este cambio no lo trae, y esa caja se
 *  pinta ANTES del primer fetch. Sin este respaldo, el aviso saldría en blanco. */
function fechaDe(b: Birthday) {
  return b.bday_date
    ? new Date(`${b.bday_date}T00:00:00`)
    : new Date(Date.now() + b.days_until * 86400000);
}
/** «6 ago» — sin día de la semana y sin el «de» que mete es-MX.
 *  El día de la semana se cayó al encoger las tarjetas: «lun 3 ago · hace 4d · 37 años» no
 *  entraba y el navegador cortaba justo la edad. Entre perder el nombre del día o perder el
 *  dato, se pierde el día — y el relativo («ayer», «en 8d») ya sitúa igual de bien. Completo,
 *  con día de la semana, va en el title al pasar el ratón. */
function fechaCorta(b: Birthday) {
  return fechaDe(b).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })
    .replace(/\./g, '').replace(' de ', ' ');
}
function fechaLarga(b: Birthday) {
  return fechaDe(b).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** Relativo O fecha, nunca los dos: en una tarjeta de cuatro por fila no caben, y juntos dicen
 *  lo mismo dos veces. Para hoy/ayer/mañana manda la palabra (nadie necesita que le recuerden
 *  qué día fue ayer); del segundo día en adelante manda la fecha, que es el dato que se busca.
 *  Con «hace 4d · 3 ago · 37 años» el navegador cortaba justo la edad. La versión completa,
 *  con día de la semana, sigue estando en el title. */
function etiquetaCorta(b: Birthday) {
  return Math.abs(b.days_until) <= 1 ? relativo(b.days_until) : fechaCorta(b);
}

/** La tarjeta de una persona. Es UNA sola para los dos estados del aviso —el banner de
 *  «hoy cumplen» y la línea de tiempo de los días vecinos— para que el diseño no se separe
 *  con el tiempo: si cambia aquí, cambia en los dos (Carlos 7-ago: «mantén el diseño»). */
function TarjetaCumple({ b, etiqueta, apagado = false, ring = '', accion = null, compacta = false,
  title, tono = 'border-line bg-surface', confeti = false, extraClass = '' }: {
  b: Birthday; etiqueta: string; apagado?: boolean; ring?: string;
  accion?: React.ReactNode; compacta?: boolean; title?: string;
  /** Clases de borde+fondo que dicen de un vistazo si es hoy, ayer o algo que viene. */
  tono?: string; confeti?: boolean; extraClass?: string;
}) {
  return (
    <div title={title}
      className={`relative min-w-0 overflow-hidden rounded-xl border ${tono} ${extraClass} ${compacta ? 'p-2' : 'p-2.5'} ${apagado ? 'opacity-70' : ''}`}>
      {confeti && (
        <span className="lm-confeti" aria-hidden="true">
          <i /><i /><i /><i /><i /><i /><i /><i />
        </span>
      )}
      {/* relative = por encima del confeti, que va en el fondo de la tarjeta. */}
      <Link to={`/alumnos/${b.account_id}`} className="relative flex min-w-0 items-center gap-2.5">
        <PersonAvatar
          contactId={b.role !== 'student' ? null : b.account_id}
          photoSrc={b.role !== 'student' ? teacherPhotoUrl(b.account_id) : undefined}
          hasPhoto={b.has_photo} name={b.firstname} lastName={b.lastname}
          size={compacta ? 'h-9 w-9' : 'h-10 w-10'} initialClass="text-sm"
          className={`rounded-full ${ring}`} />
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-content">{b.firstname} {b.lastname}</span>
            {b.role !== 'student' && <span className="shrink-0 rounded-full bg-brand-blue/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-blue">{b.role === 'teacher' ? 'Profe' : 'Equipo'}</span>}
          </div>
          <div className="truncate text-xs text-content-3">{etiqueta}</div>
          {/* Las clases solo en el banner de HOY, donde se va a escribir a la persona y viene bien
              saber de qué grupo es. En la tira de días vecinos las quitó Carlos (7-ago): cada
              chip es una línea más de alto en algo que solo debe informar de pasada. */}
          {!compacta && <ClassChips classes={b.classes} max={2} />}
        </div>
      </Link>
      {accion && <div className="mt-2 flex justify-end">{accion}</div>}
    </div>
  );
}

/** Aviso de cumpleaños en el Home, orientado a acción:
 *  - Si hay cumpleañeros HOY sin felicitar → banner destacado con botón Felicitar (al felicitar, desaparece).
 *  - Si no hay pendientes de hoy → línea de tiempo de los días vecinos (los que acaban de pasar y
 *    los que vienen) + acceso al muralito del mes. Antes aquí solo decía «Hoy no cumple nadie»,
 *    que en la práctica es una barra ocupando ancho para no informar de nada (Carlos 7-ago). */
export default function BirthdayAlert() {
  // Pinta al instante lo último conocido del MISMO día y refresca por detrás.
  // El sufijo v2 tira los cachés de la versión anterior: no traen bday_date ni días negativos.
  const cacheKey = `birthdays:v2:${todayLocal()}`;
  const [bdays, setBdays] = useState<Birthday[] | null>(() => readCache<Birthday[]>(cacheKey) ?? null);

  useEffect(() => {
    getBirthdays(14, DIAS_ATRAS).then((r) => {
      setBdays(r.data);
      writeCache(cacheKey, r.data);
      pruneCacheGroup('birthdays', cacheKey);
    }).catch(() => setBdays((prev) => prev ?? []));
  }, []);

  if (bdays === null) return null;

  const todayAll = bdays.filter((b) => b.days_until === 0);
  const pending = todayAll.filter((b) => !b.greeted);

  // ===== UN SOLO FORMATO, siempre =====
  // Antes, si alguien cumplía hoy y estaba sin felicitar, esto se convertía en un banner de 176px
  // con esa persona sola y los demás desaparecían. Carlos lo vio con su propio cumpleaños el
  // 7-ago: «me muestra en gigante… debería seguir mostrando los otros contactos». Ahora quien
  // cumple hoy es una tarjeta más de la misma barra; lo que lo distingue es el color y el
  // confeti, no ocupar el sitio de los demás.
  //
  // Vienen ordenados por days_until, o sea ayer → hoy → los que faltan: se lee como una línea de
  // tiempo. Quien cumplió ayer va atenuado salvo que nadie lo haya felicitado.
  const vecinos = bdays.slice(0, MAX_TARJETAS);
  const sobran = bdays.length - vecinos.length;

  // Esta caja va en la CABECERA del Home, encima del calendario: aquí el espacio no es gratis,
  // se lo quita a lo que el estudio abre el sistema para ver. Por eso el título va en la misma
  // línea que las tarjetas en vez de encima, y las tarjetas no llevan ni chips de clase ni
  // botón (Carlos 7-ago). Alto resultante ~76px contra los 312px de la primera versión.
  return (
    <div className="rounded-xl border border-line bg-surface p-2.5 shadow-sm">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="flex shrink-0 items-center justify-between gap-2 lg:w-44 lg:flex-col lg:items-start lg:justify-center lg:gap-0">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-content">
            <Cake size={15} className="text-accent-ink" /> Cumpleaños
          </h3>
          <span className="truncate text-[11px] text-content-3">
            {pending.length > 0
              ? <span className="font-semibold text-accent-ink">
                  {pending.length === 1 ? '¡hoy cumple alguien!' : `¡hoy cumplen ${pending.length}!`}
                </span>
              : todayAll.length > 0
                ? <><Check size={12} className="-mt-0.5 inline text-emerald-500" /> hoy ya felicitados</>
                : 'hoy no cumple nadie'}
          </span>
        </div>

        {vecinos.length === 0 ? (
          <p className="text-xs text-content-3">Tampoco hay ninguno cerca — el próximo aparecerá aquí solo.</p>
        ) : (
          // 4 columnas ya en lg (no en xl): con 3 quedaba una segunda fila casi vacía.
          <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {vecinos.map((b) => {
              const paso = b.days_until < 0;
              // El anillo ámbar marca a quien cumplió y nadie felicitó. Ya no lleva botón —Carlos
              // lo retiró el 7-ago—, así que la felicitación sale de su ficha; la marca se queda
              // porque señalar el pendiente no cuesta un solo píxel de alto.
              const hoy = b.days_until === 0;
              const pendiente = (paso || hoy) && !b.greeted;
              return (
                // El relativo va PRIMERO: es lo que se busca al pasar la vista por la barra.
                <TarjetaCumple key={b.account_id} b={b} compacta confeti={hoy}
                  apagado={paso && !pendiente}
                  extraClass={hoy ? 'lm-cumple-hoy' : ''}
                  tono={hoy ? TONO.hoy : paso ? TONO.ayer : TONO.proximo}
                  ring={pendiente ? (hoy ? 'ring-2 ring-accent/60' : 'ring-2 ring-amber-400/60') : ''}
                  etiqueta={`${etiquetaCorta(b)} · ${b.turns} años`}
                  title={`${fechaLarga(b)} · cumple ${b.turns} años${pendiente ? ' · sin felicitar' : ''}`} />
              );
            })}
          </div>
        )}

        <Link to="/cumpleanos" className="shrink-0 self-end text-xs font-medium text-brand-blue hover:underline lg:self-center">
          {sobran > 0 ? `+${sobran} más →` : 'Ver del mes →'}
        </Link>
      </div>
    </div>
  );
}
